import { useState, useRef, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { Circle, Coffee, Moon, Sun, MinusCircle } from 'lucide-react';

export function StatusSelector() {
    const { currentUser, updateStatus } = useApp();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (!currentUser) return null;

    const statuses = [
        { type: 'online', label: 'Online', icon: Circle, color: '#10b981', text: '' },
        { type: 'away', label: 'Away', icon: Moon, color: '#f59e0b', text: '' },
        { type: 'dnd', label: 'Do Not Disturb', icon: MinusCircle, color: '#ef4444', text: '' },
        { type: 'lunch', label: 'Out for Lunch', icon: Coffee, color: '#d97706', text: 'Lunch' },
        { type: 'vacation', label: 'On Vacation', icon: Sun, color: '#0ea5e9', text: 'Vacation' },
        { type: 'offline', label: 'Offline', icon: Circle, color: '#9ca3af', text: '' },
    ] as const;

    const currentStatus = statuses.find(s => s.type === currentUser.status_type) || statuses[0];

    const handleSelect = (type: any, text: string) => {
        updateStatus(type, text);
        setIsOpen(false);
    };

    return (
        <div className="status-selector" ref={containerRef} style={{ position: 'relative' }}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 8px',
                    borderRadius: 4,
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    color: 'var(--text-secondary)',
                    fontSize: 12
                }}
            >
                <div style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: currentStatus.color,
                    boxShadow: '0 0 0 2px var(--bg-primary)'
                }} />
                <span style={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {currentUser.status_text || currentStatus.label}
                </span>
            </button>

            {isOpen && (
                <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 0,
                    width: 180,
                    marginBottom: 8,
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    padding: 4,
                    zIndex: 100
                }}>
                    {statuses.map(s => (
                        <button
                            key={s.type}
                            onClick={() => handleSelect(s.type, s.text)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                width: '100%',
                                padding: '8px 12px',
                                border: 'none',
                                background: currentUser.status_type === s.type ? 'var(--bg-tertiary)' : 'transparent',
                                borderRadius: 6,
                                cursor: 'pointer',
                                textAlign: 'left',
                                color: 'var(--text-primary)',
                                fontSize: 13
                            }}
                        >
                            <s.icon size={14} color={s.color} fill={s.type === 'online' || s.type === 'offline' ? s.color : 'none'} />
                            {s.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
